'use client';

import type { CSSProperties, RefObject } from 'react';
import { useEffect, useState } from 'react';

import { cn, FloatingButton } from '@yanolja-rnd/nol-design-system';
import { IcArrowlessFill } from '@yanolja-rnd/nol-icons';
import throttle from 'lodash-es/throttle';

export interface ScrollToTopButtonProps {
  top?: number; // 맨 위로 버튼을 노출하기 위한 픽셀 값
  scrollBehavior?: 'auto' | 'smooth' | undefined; // 스크롤 동작 처리 방식.
  targetRef?: RefObject<HTMLElement | null>;
  onClick?: () => void;
  className?: string;
  hasGnb?: boolean;
  style?: CSSProperties;
}

/**
 * ScrollToTopButton
 * - 스크롤 시 화면 하단에 노출되는 '맨 위로' 버튼 컴포넌트 기존 GoToTopButton 컴포넌트를 Tailwind 및 NDS 버튼을 사용하여 구현
 */

export const ScrollToTopButton = ({
  top = 150,
  scrollBehavior = 'smooth',
  targetRef,
  onClick,
  className,
  hasGnb = false,
  style,
}: ScrollToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = targetRef?.current || window;

    const handleScroll = throttle(() => {
      const scrollPosition = targetRef?.current?.scrollTop ?? window.scrollY;
      setIsVisible(scrollPosition > top);
    }, 100);

    handleScroll();
    target.addEventListener('scroll', handleScroll);

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [top, targetRef?.current]);

  const handleClick = (): void => {
    (targetRef?.current || window).scrollTo({ top: 0, behavior: scrollBehavior });
    onClick?.();
  };

  return (
    <div
      className={cn(
        'max-w-mobile-size fixed pc:bottom-24 transition-all duration-300 z-10 left-1/2 -translate-x-1/2 w-full pc:max-w-[none]',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-100 opacity-0',
        hasGnb ? 'bottom-80' : 'bottom-16',
        className,
      )}
      style={style}
    >
      <div className="absolute right-16 pc:right-24 bottom-0 pb-[env(safe-area-inset-bottom)]">
        <FloatingButton icon={<IcArrowlessFill height={24} width={24} />} onClick={handleClick} />
      </div>
    </div>
  );
};
